import React, { memo } from 'react';
import { useSelector } from 'react-redux';
import { UserBadge } from '../../UserBadge';
import { getUser } from '../../../bundles/ad';
import { SEVERITY_COLORS } from './constants';
import { formatSiteDatetime } from '../../../datetimeUtils';
import { USER_EVENTS } from '../../../constants';
import config from '../../../config';

export const ALARM_TABS = {
  NEW: 0,
  ACKNOWLEDGED: 1,
  RESOLVED: 2,
};

export const buildAlarmQuery = ({
  site,
  state,
  from,
  to,
  sortBy,
  sortOrder,
  offset,
  limit,
  deviatingReferenceDesignations,
}) => {
  const query = {
    site: site?.id,
    org: site?.org,
    state,
    from,
    to,
    sortBy,
    sortOrder,
  };

  if (offset !== undefined) {
    query.offset = offset;
  }

  if (limit !== undefined) {
    query.limit = limit;
  }

  if (deviatingReferenceDesignations !== undefined) {
    query.deviatingReferenceDesignations = deviatingReferenceDesignations;
  }

  if (config.onPremise) {
    // Default values used when processing events and alarms on-premise
    delete query.site;
    delete query.org;
  }

  return query;
};

export const DGA_GAS_NAMES = [
  'hydrogen',
  'oxygen',
  'nitrogen',
  'methane',
  'carbon monoxide',
  'carbon dioxide',
  'ethylene',
  'ethane',
  'acetylene',
  'moisture',
];

export const formatAlarmName = (alarm) => {
  const algorithmName = alarm.algorithmName || '';
  const algorithmType = alarm.algorithmType || '';
  const identifier = alarm.additionalIdentifier;
  const shouldIncludeIdentifier = identifier && DGA_GAS_NAMES.includes(identifier.toLowerCase());
  const algorithmTypePart = algorithmType ? ` (${algorithmType})` : '';
  return `${algorithmName}${shouldIncludeIdentifier ? `: ${identifier}` : ''}${algorithmTypePart}`;
};

export const filterAlarmsBySearch = (alarms, searchText) => {
  if (!alarms) return [];
  if (!searchText) return alarms;

  const searchLower = searchText.toLowerCase();
  const matchesSearch = (value) =>
    typeof value === 'string' && value.toLowerCase().includes(searchLower);

  return alarms.filter((alarm) => {
    const displayAlarmName = formatAlarmName(alarm);

    return (
      matchesSearch(displayAlarmName) ||
      matchesSearch(alarm.deviatingReferenceDesignation) ||
      matchesSearch(alarm.algorithmName) ||
      matchesSearch(alarm.message)
    );
  });
};

export const UserAvatar = memo(({ userId }) => {
  const user = useSelector((state) => getUser(state, userId));
  if (!user)
    return (
      <UserBadge
        first="Anonymous"
        last="User"
      />
    );
  return (
    <UserBadge
      first={user.firstName}
      last={user.lastName}
    />
  );
});

export const UserSignature = memo(({ userId }) => {
  const user = useSelector((state) => getUser(state, userId));
  if (!user) return <div>Anonymous User</div>;
  return <div>{user.name}</div>;
});

export const getSeverityColor = (severity) => {
  if (!severity) return '#666';
  const key = severity.toLowerCase();
  // Map alarm severity values to color keys
  // Alarms use: critical, warning, info, etc.
  // Colors use: alert, warning, normal, unknown
  let colorKey = key;
  if (key === 'critical') colorKey = 'alert';
  else if (key === 'info') colorKey = 'normal';
  return SEVERITY_COLORS[colorKey] || '#666';
};

export const formatDate = (dateValue, timezone) => {
  if (!dateValue) return '-';
  return formatSiteDatetime(dateValue, timezone);
};

export const DEFAULT_COLUMN = {
  minWidth: 50,
  width: 100,
};

export const getEventTypeColor = (state) => {
  const eventType = USER_EVENTS.find((event) => event.id === state);
  return eventType?.color || '#B3B3B3';
};

export const getEventTypeLabel = (state) => {
  const eventType = USER_EVENTS.find((event) => event.id === state);
  return eventType?.label || state;
};
